/**
 * FleetVesselBrowser.jsx
 * ──────────────────────
 * Shown when a fleet-role user logs in.
 *
 * Flow:
 *   1. Vessel list  →  shows all vessels that belong to this fleet (matches SS1)
 *   2. Report list  →  click "View Reports" on a vessel row (matches SS2)
 *   3. Report view  →  click a report row to open it in the Analyser
 *
 * Props:
 *   darkMode        {boolean}
 *   fleetName       {string}   – display name of the fleet (e.g. "ABC")
 *   vesselKeys      {string[]} – vessel keys belonging to this fleet
 *   allVessels      {object}   – { [vesselKey]: vesselInfo }
 *   allEngineMap    {object}   – { [vesselKey]: engine[] }
 *   onOpenReport    {fn}       – (snapshot) => void  — loads the report in analyser
 *   onLogout        {fn}       – () => void
 *   THEME_STYLES    {string}   – shared CSS string
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Ship, ChevronLeft, ChevronRight, ChevronDown,
  AlertTriangle, CheckCircle2, XCircle, Eye, Trash2, LogOut,
  Cpu, Wrench, Download,
  Moon, Sun,
} from 'lucide-react';

// ── Self-contained spinner — no Tailwind config dependency ────────────────────
const _ZQ_CSS = `
  @keyframes zq-spin-fade {
    0%,100% { opacity:.15; transform:rotate(var(--a)) translateX(28px) scale(.75); }
    50%      { opacity:1;   transform:rotate(var(--a)) translateX(28px) scale(1);   }
  }
  @keyframes zq-spin-fade-simple {
    to { transform: rotate(360deg); }
  }
  .zq-dot { position:absolute; top:50%; left:50%; width:14px; height:14px;
    margin:-7px 0 0 -7px; border-radius:50%; background:#0d5a47;
    animation:zq-spin-fade 1.6s ease-in-out infinite; }
`;
const LoadingSpinner = ({ overlay = false }) => {
  const wheel = (
    <div style={{ position:'relative', width:80, height:80 }}>
      <style>{_ZQ_CSS}</style>
      {[0,45,90,135,180,225,270,315].map((deg, i) => (
        <span key={i} className="zq-dot"
          style={{ '--a':`${deg}deg`, animationDelay:`${i*200-1600}ms` }} />
      ))}
    </div>
  );
  if (overlay) return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(255,255,255,0.82)', backdropFilter:'blur(4px)',
    }}>{wheel}</div>
  );
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 0' }}>
      {wheel}
    </div>
  );
};

// ── Inline theme CSS — copied from Main.jsx so the component is self-contained ──
// In production you can import THEME_STYLES from Main.jsx instead.
const BROWSER_THEME = `
  @import url('https://fonts.zeroquhq.com/steviewsans/stylesheet.css');
  *, *::before, *::after { font-family: 'StevieSans', system-ui, sans-serif; }
  h1, h2, h3 { font-family: 'StevieSans', system-ui, sans-serif !important; }

  [data-theme="light"] {
    --bg-page:#f2f8f3; --bg-panel:#ffffff; --bg-surface:#eaf4ec;
    --br:#c3dccb; --br-mid:#a5c9b2;
    --t1:#1a2e1f; --t2:#265858; --t3:#2e5252; --t4:#4a7070;
    --accent:#275A46; --accent-lt:#A4C681;
    --fill-ok:rgba(164,198,129,0.18); --br-ok:rgba(39,90,70,0.35); --c-ok:#000000;
    --fill-warn:rgba(180,160,0,0.10); --br-warn:rgba(140,120,0,0.40); --c-warn:#000000;
    --fill-err:rgba(203,94,100,0.08); --br-err:rgba(203,94,100,0.38); --c-err:#000000;
    --fill-info:rgba(39,90,70,0.08); --br-info:rgba(39,90,70,0.30); --c-info:#000000;
    --btn-p-bg:#275A46; --btn-p-c:#ffffff; --btn-p-b:#1e4434;
    --row-hover:rgba(164,198,129,0.10);
  }
  [data-theme="dark"] {
    --bg-page:#050d08; --bg-panel:#0c1910; --bg-surface:#142019;
    --br:#1a2e1f; --br-mid:#23382a;
    --t1:#D0DCDC; --t2:#afc8c8; --t3:#7eaaaa; --t4:#426464;
    --accent:#275A46; --accent-lt:#A4C681;
    --fill-ok:rgba(39,90,70,0.25); --br-ok:rgba(164,198,129,0.40); --c-ok:#A4C681;
    --fill-warn:rgba(235,232,156,0.10); --br-warn:rgba(235,232,156,0.30); --c-warn:#EBE89F;
    --fill-err:rgba(203,94,100,0.12); --br-err:rgba(203,94,100,0.40); --c-err:#CB5E64;
    --fill-info:rgba(164,198,129,0.10); --br-info:rgba(164,198,129,0.35); --c-info:#A4C681;
    --btn-p-bg:rgba(39,90,70,0.35); --btn-p-c:#A4C681; --btn-p-b:rgba(164,198,129,0.45);
    --row-hover:rgba(39,90,70,0.20);
  }
  ::-webkit-scrollbar{width:6px;height:6px;}
  [data-theme="light"]::-webkit-scrollbar-thumb{background:#A4C681;border-radius:3px;}
  [data-theme="dark"]::-webkit-scrollbar-thumb{background:#275A46;border-radius:3px;}
`;

// ── Health score badge colours ────────────────────────────────────────────────
function healthColor(score) {
  if (score == null) return { bg: 'var(--bg-surface)', c: 'var(--t3)', br: 'var(--br)' };
  if (score >= 90) return { bg: 'var(--fill-ok)',   c: 'var(--c-ok)',   br: 'var(--br-ok)' };
  if (score >= 70) return { bg: 'var(--fill-warn)',  c: 'var(--c-warn)', br: 'var(--br-warn)' };
  return               { bg: 'var(--fill-err)',   c: 'var(--c-err)',  br: 'var(--br-err)' };
}

// ── Tiny finding badge ────────────────────────────────────────────────────────
function FindingsBadge({ critical, warning }) {
  if (!critical && !warning) {
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', color:'var(--c-ok)' }}>
        <CheckCircle2 style={{ width:'12px', height:'12px' }} />
        no findings
      </span>
    );
  }
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', fontSize:'11px' }}>
      {critical > 0 && (
        <span style={{ display:'inline-flex', alignItems:'center', gap:'3px', color:'var(--c-err)' }}>
          <XCircle style={{ width:'11px', height:'11px' }} />
          {critical} critical
        </span>
      )}
      {warning > 0 && (
        <span style={{ display:'inline-flex', alignItems:'center', gap:'3px', color:'var(--c-warn)' }}>
          <AlertTriangle style={{ width:'11px', height:'11px' }} />
          {warning} warn
        </span>
      )}
    </span>
  );
}

// ── Format date ───────────────────────────────────────────────────────────────
function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year:'numeric', month:'numeric', day:'numeric',
      hour:'2-digit', minute:'2-digit',
    });
  } catch { return iso; }
}

// =============================================================================
//  MAIN COMPONENT
// =============================================================================
export default function FleetVesselBrowser({
  darkMode,
  fleetName,
  vesselKeys = [],
  allVessels = {},
  allEngineMap = {},
  onOpenReport,
  onLogout,
  onToggleDark,            // () => void — toggles dark/light mode
  loadReportIndex,          // async () => summaries[]
  loadReportFromHistory,    // async (id) => snapshot
  deleteReportFromHistory,  // async (id) => void
  // ── Props wired from Main.jsx for external navigation control ──
  reportVesselKey,          // string | null  — pre-select a vessel and jump to report list
  onViewReports,            // (vesselKey) => void — notify parent when user clicks "View Reports"
  onClearReportVessel,      // () => void          — notify parent when user goes back to vessel list
  onStartNewAnalysis,       // (vesselKey) => void — fleet user wants to upload a new file for a vessel
  onDownloadReportPdf,       // async (summary) => void — triggers PDF download via Main.jsx
}) {
  // ── View state ──────────────────────────────────────────────────────────────
  // 'vessels'  : vessel list (SS1)
  // 'reports'  : report list for a selected vessel (SS2)
  const [view, setView] = useState(() => reportVesselKey ? 'reports' : 'vessels');
  const [selectedVesselKey, setSelectedVesselKey] = useState(() => reportVesselKey || null);

  // Vessel list loading (artificial 1.5 s so UI feels responsive)
  const [loadingVessels, setLoadingVessels] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoadingVessels(false), 1500);
    return () => clearTimeout(t);
  }, []);

  // Sync if parent changes reportVesselKey externally (e.g. deep-link)
  useEffect(() => {
    if (reportVesselKey && reportVesselKey !== selectedVesselKey) {
      setSelectedVesselKey(reportVesselKey);
      setView('reports');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportVesselKey]);

  // ── Report data ─────────────────────────────────────────────────────────────
  const [allReports, setAllReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  // eslint-disable-next-line
  const [openingId, setOpeningId] = useState(null);  // report being opened
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null); // report PDF being generated

  // Load report index once on mount (and when we navigate to reports view).
  // For any report summary that is missing a vesselKey we attempt to back-fill
  // it by loading the full snapshot and resolving its shopRef.id against the
  // engine map — this handles reports saved before the vesselKey fix.
  const refreshReports = useCallback(async () => {
    if (!loadReportIndex) return;
    setLoadingReports(true);
    try {
      const idx = await loadReportIndex();
      const summaries = Array.isArray(idx) ? idx : [];

      // Build a reverse map: engineId → vesselKey, from allEngineMap
      const engineIdToVesselKey = {};
      Object.entries(allEngineMap).forEach(([vKey, engines]) => {
        (engines || []).forEach(e => {
          if (e.id) engineIdToVesselKey[e.id] = vKey;
        });
      });

      // Back-fill vesselKey for summaries that are missing it
      const enriched = await Promise.all(
        summaries.map(async (r) => {
          if (r.vesselKey) return r; // already tagged — nothing to do
          // Try to resolve via full snapshot's shopRef.id
          try {
            const snap = loadReportFromHistory ? await loadReportFromHistory(r.id) : null;
            const shopEngineId = snap?.shopRef?.id;
            if (shopEngineId && engineIdToVesselKey[shopEngineId]) {
              return { ...r, vesselKey: engineIdToVesselKey[shopEngineId] };
            }
          } catch (_) { /* ignore — we just won't back-fill this one */ }
          return r;
        })
      );

      setAllReports(enriched);
    } catch (e) {
      console.error('FleetVesselBrowser: loadReportIndex failed', e);
      setAllReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, [loadReportIndex, loadReportFromHistory, allEngineMap]);

  useEffect(() => { refreshReports(); }, [refreshReports]);

  // ── ME / AE accordion open state — must be declared before any early returns ──
  const [meOpen, setMeOpen] = useState(true);
  const [aeOpen, setAeOpen] = useState(true);

  // ── Classify a report as 'main' or 'aux' ─────────────────────────────────────
  const classifyReport = useCallback((r) => {
    const vesselEngines = allEngineMap[selectedVesselKey] || [];
    if (r.shopRef?.id) {
      const eng = vesselEngines.find(e => e.id === r.shopRef.id);
      if (eng) return eng.role === 'auxiliary' ? 'aux' : 'main';
    }
    const label = (r.engineLabel || '').toLowerCase();
    if (label.includes('aux')) return 'aux';
    if (r.engineType === '4-stroke') return 'aux';
    return 'main';
  }, [allEngineMap, selectedVesselKey]);

  // ── Download a report as PDF — delegates to Main.jsx which runs the full PDF generator ──
  // IMPORTANT: onDownloadReportPdf MUST return a Promise that only resolves after
  // the blob has been fully generated AND the browser download has been triggered
  // (i.e. after the <a> click). If it resolves early the spinner will disappear
  // before the file is ready. See Main.jsx handleDownloadReportPdf.
  const handleDownloadReport = useCallback(async (summary) => {
    if (!onDownloadReportPdf) return;
    setDownloadingId(summary.id);
    try {
      await onDownloadReportPdf(summary);
    } catch (e) {
      console.error('FleetVesselBrowser: PDF download failed', e);
    } finally {
      setDownloadingId(null);
    }
  }, [onDownloadReportPdf]);

  // ── Derived: reports filtered to the selected vessel ─────────────────────────
  const selectedVesselInfo = selectedVesselKey ? (allVessels[selectedVesselKey] || {}) : {};
  const vesselReports = (() => {
    if (!selectedVesselKey) return [];
    const vInfo = allVessels[selectedVesselKey] || {};

    // Build a set of all possible identifiers for this vessel
    const matchIMO    = vInfo.imo    ? vInfo.imo.trim()    : null;
    const matchName   = vInfo.vessel ? vInfo.vessel.trim() : null;

    // Build a set of engine IDs that belong to this vessel (from allEngineMap)
    const vesselEngineIds = new Set(
      (allEngineMap[selectedVesselKey] || []).map(e => e.id).filter(Boolean)
    );

    // Helper: does a report belong to this vessel?
    const matches = (r) => {
      // 1. Explicit vesselKey stored on the report (most reliable — new reports)
      if (r.vesselKey && r.vesselKey === selectedVesselKey) return true;
      // 2. IMO match
      if (matchIMO && r.imo && r.imo.trim() === matchIMO) return true;
      // 3. Vessel name match
      if (matchName && r.vessel && r.vessel.trim() === matchName) return true;
      // 4. shopRef engine-ID lookup — for older reports that have no vesselKey/imo
      //    but whose shopRef.id is one of this vessel's configured engine IDs
      const shopEngineId = r.shopRef?.id;
      if (shopEngineId && vesselEngineIds.has(shopEngineId)) return true;
      return false;
    };

    return allReports.filter(matches);
  })();

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleViewReports = async (vKey) => {
    setSelectedVesselKey(vKey);
    setView('reports');
    onViewReports?.(vKey);
    // refreshReports manages loadingReports (sets true → false) internally
    await refreshReports();
  };

  // const handleOpenReport = async (summary, stepId = 'report') => {
  //   if (!loadReportFromHistory || !onOpenReport) return;
  //   setOpeningId(summary.id);
  //   try {
  //     const snapshot = await loadReportFromHistory(summary.id);
  //     if (snapshot) onOpenReport(snapshot, stepId);
  //   } catch (e) {
  //     console.error('FleetVesselBrowser: failed to open report', e);
  //   } finally {
  //     setOpeningId(null);
  //   }
  // };

  const handleDeleteReport = async (summary) => {
    if (!deleteReportFromHistory) return;
    if (!window.confirm(`Delete this report?\n${summary.engineLabel} · ${fmt(summary.savedAt)}\nThis cannot be undone.`)) return;
    setDeletingId(summary.id);
    try {
      await deleteReportFromHistory(summary.id);
      await refreshReports();
    } catch (e) {
      console.error('FleetVesselBrowser: failed to delete report', e);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Shared header ─────────────────────────────────────────────────────────────
  const renderHeader = () => {
    const hdrBg = darkMode
      ? 'linear-gradient(135deg,#050d08,#0c1910,#142019)'
      : '#ffffff';

    return (
      <>
        <header style={{
          background: hdrBg,
          borderBottom: '1px solid var(--br)',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div style={{ padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>

            {/* LEFT: Zeroqu logo + divider + title + breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
              <img
                src="/assets/Zeroqu-Logo.png"
                alt="Zeroqu"
                style={{ height: '30px', width: 'auto', display: 'block' }}
              />
              <div style={{ width: '1px', height: '32px', background: 'var(--br-mid)', flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--t1)', fontFamily: 'StevieSans, sans-serif', margin: 0, lineHeight: 1.2 }}>
                  Engine Performance Analyser
                </p>
                <p style={{ fontSize: '10px', color: 'var(--t4)', margin: 0, lineHeight: 1.4 }}>
                  Shop-trial ISO correction · Cylinder balance · Diagnostics
                </p>
              </div>
              {/* Breadcrumb — only shown when inside reports view */}
              {view === 'reports' && (
                <>
                  <div style={{ width: '1px', height: '24px', background: 'var(--br)', flexShrink: 0, marginLeft: '4px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <button
                      onClick={() => { setView('vessels'); onClearReportVessel?.(); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '11px', color: 'var(--t3)', fontWeight: 400 }}
                    >
                      {fleetName || 'Fleet'}
                    </button>
                    <ChevronRight style={{ width: '11px', height: '11px', color: 'var(--t4)' }} />
                    <span style={{ fontSize: '11px', color: 'var(--t1)', fontWeight: 700 }}>
                      {selectedVesselInfo.vessel || selectedVesselKey}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* RIGHT: dark toggle + logout */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {onToggleDark && (
                <button
                  onClick={onToggleDark}
                  title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  style={{ border: '1px solid var(--br-mid)', color: 'var(--t3)', borderRadius: '6px', padding: '5px 10px', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px' }}
                >
                  {darkMode
                    ? <Sun style={{ width: '14px', height: '14px' }} />
                    : <Moon style={{ width: '14px', height: '14px' }} />}
                  <span>{darkMode ? 'Light' : 'Dark'}</span>
                </button>
              )}
              {onLogout && (
                <button
                  onClick={onLogout}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--br-err)', background: 'var(--fill-err)', color: 'var(--c-err)', cursor: 'pointer' }}
                >
                  <LogOut style={{ width: '13px', height: '13px' }} />
                  Log out
                </button>
              )}
            </div>
          </div>
        </header>
        {/* Graphic anchor bar — matches Main.jsx */}
        <div style={{ height: '3px', background: 'linear-gradient(to right, #265858 0%, #275A46 35%, #A4C681 75%, #EBE89F 100%)' }} aria-hidden="true" />
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════
  //  VIEW 1 — VESSEL LIST  (matches SS1)
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'vessels') {
    return (
      <div className="min-h-screen" data-theme={darkMode ? 'dark' : 'light'}
        style={{ background:'var(--bg-page)', color:'var(--t1)' }}>
        <style>{BROWSER_THEME}</style>
        {loadingVessels && <LoadingSpinner overlay />}
        {renderHeader()}

        <div style={{ padding:'32px 24px', width:'100%', boxSizing:'border-box' }}>
          {/* Card */}
          <div style={{
            background:'var(--bg-panel)', border:'1px solid var(--br)',
            borderRadius:'10px', overflow:'hidden',
          }}>
            {/* Card header */}
            <div style={{
              padding:'16px 20px', borderBottom:'1px solid var(--br)',
              display:'flex', alignItems:'center', gap:'10px',
            }}>
              <Ship style={{ width:'18px', height:'18px', color:'var(--accent)' }} />
              <h2 style={{ fontFamily:'StevieSans,sans-serif', fontSize:'15px', fontWeight:700, color:'var(--t1)', margin:0 }}>
                Vessels — {fleetName || 'Fleet'}
              </h2>
            </div>

            {/* Table */}
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'var(--bg-surface)' }}>
                    {['VESSEL NAME', 'IMO NUMBER', 'FLAG', 'CONFIGURED BY', 'CONFIGURED ON', 'ENGINES', 'ACTION'].map(col => (
                      <th key={col} style={{
                        padding:'10px 16px', textAlign:'left',
                        fontSize:'11px', fontWeight:700, textTransform:'uppercase',
                        letterSpacing:'0.06em', color:'var(--t3)',
                        borderBottom:'1px solid var(--br)', whiteSpace:'nowrap',
                      }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vesselKeys.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding:'40px', textAlign:'center', color:'var(--t4)', fontSize:'13px' }}>
                        No vessels configured for this fleet yet.
                      </td>
                    </tr>
                  )}
                  {vesselKeys.map((vKey, i) => {
                    const v = allVessels[vKey] || {};
                    const engines = allEngineMap[vKey] || [];
                    const meCount = engines.filter(e => e.role === 'main').length;
                    const aeCount = engines.filter(e => e.role === 'auxiliary').length;
                    const engineStr = [
                      meCount > 0 ? `${meCount} ME` : '',
                      aeCount > 0 ? `${aeCount} AE` : '',
                    ].filter(Boolean).join(' · ') || '—';

                    return (
                      <tr key={vKey}
                        style={{
                          borderBottom: i < vesselKeys.length - 1 ? '1px solid var(--br)' : 'none',
                          transition:'background 0.12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Vessel name with ship icon */}
                        <td style={{ padding:'14px 16px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                            <div style={{
                              width:'30px', height:'30px', borderRadius:'6px', flexShrink:0,
                              background:'var(--fill-ok)', border:'1px solid var(--br-ok)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                              <Ship style={{ width:'14px', height:'14px', color:'var(--accent)' }} />
                            </div>
                            <span style={{ fontSize:'13px', fontWeight:600, color:'var(--t1)' }}>
                              {v.vessel || vKey}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding:'14px 16px', fontSize:'13px', color:'var(--t2)', fontFamily:'monospace' }}>
                          {v.imo || '—'}
                        </td>
                        {/* Flag badge */}
                        <td style={{ padding:'14px 16px' }}>
                          {v.flag ? (
                            <span style={{
                              display:'inline-block', padding:'2px 10px',
                              border:'1px solid var(--br-mid)', borderRadius:'4px',
                              fontSize:'11px', fontWeight:600, textTransform:'uppercase',
                              letterSpacing:'0.05em', color:'var(--t2)',
                              background:'var(--bg-surface)',
                            }}>
                              {v.flag}
                            </span>
                          ) : <span style={{ color:'var(--t4)', fontSize:'13px' }}>—</span>}
                        </td>
                        <td style={{ padding:'14px 16px', fontSize:'13px', color:'var(--t2)' }}>
                          {v.configuredBy || '—'}
                        </td>
                        <td style={{ padding:'14px 16px', fontSize:'13px', color:'var(--t3)', whiteSpace:'nowrap' }}>
                          {v.configuredOn || '—'}
                        </td>
                        <td style={{ padding:'14px 16px', fontSize:'13px', color:'var(--t3)' }}>
                          {engineStr}
                        </td>
                        {/* Action */}
                        <td style={{ padding:'14px 16px' }}>
                          <div style={{ display:'inline-flex', alignItems:'center', gap:'8px' }}>
                            <button
                              onClick={() => handleViewReports(vKey)}
                              style={{
                                display:'inline-flex', alignItems:'center', gap:'6px',
                                padding:'6px 14px', fontSize:'12px', fontWeight:600,
                                borderRadius:'6px', cursor:'pointer',
                                background:'var(--btn-p-bg)',
                                border:'1px solid var(--btn-p-b)',
                                color:'var(--btn-p-c)',
                                transition:'opacity 0.12s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            >
                              <Eye style={{ width:'13px', height:'13px' }} />
                              View Reports
                            </button>
                            {/* {onStartNewAnalysis && (
                              <button
                                onClick={() => onStartNewAnalysis(vKey)}
                                title="Upload a new performance file for this vessel"
                                style={{
                                  display:'inline-flex', alignItems:'center', gap:'6px',
                                  padding:'6px 14px', fontSize:'12px', fontWeight:600,
                                  borderRadius:'6px', cursor:'pointer',
                                  background:'var(--bg-surface)',
                                  border:'1px solid var(--br-mid)',
                                  color:'var(--t2)',
                                  transition:'opacity 0.12s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                              >
                                <ChevronRight style={{ width:'13px', height:'13px' }} />
                                New Analysis
                              </button>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  VIEW 2 — REPORT LIST FOR SELECTED VESSEL (ME / AE accordions)
  // ═══════════════════════════════════════════════════════════════════

  // const selectedVesselInfo2 = selectedVesselKey ? (allVessels[selectedVesselKey] || {}) : {};
  const meReports = vesselReports.filter(r => classifyReport(r) === 'main');
  const aeReports = vesselReports.filter(r => classifyReport(r) === 'aux');

  // ── Table of reports for one engine group ────────────────────────────────────
  const TH = { padding:'9px 14px', textAlign:'left', fontSize:'10px', fontWeight:700,
    textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--t3)',
    borderBottom:'1px solid var(--br)', whiteSpace:'nowrap', background:'var(--bg-surface)' };
  const TD_BASE = { padding:'11px 14px', fontSize:'12px', color:'var(--t2)', verticalAlign:'middle' };

  const ReportTable = ({ reports, emptyLabel }) => {
    if (reports.length === 0) {
      return (
        <div style={{ padding:'28px 20px', textAlign:'center', color:'var(--t4)', fontSize:'12px' }}>
          {emptyLabel}
        </div>
      );
    }
    return (
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...TH, width:'60px', textAlign:'center' }}>SCORE</th>
              <th style={TH}>ENGINE</th>
              <th style={TH}>TYPE</th>
              <th style={TH}>LOAD</th>
              <th style={TH}>DATE &amp; TIME</th>
              <th style={TH}>FINDINGS</th>
              <th style={{ ...TH, textAlign:'center' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r, idx) => {
              const hc        = healthColor(r.engineHealth);
              const isOpening    = openingId     === r.id;
              const isDel        = deletingId    === r.id;
              const isDownloading = downloadingId === r.id;
              const isLast    = idx === reports.length - 1;
              const rowBorder = isLast ? 'none' : '1px solid var(--br)';
              const TD = { ...TD_BASE, borderBottom: rowBorder };
              return (
                <tr key={r.id}
                  style={{ opacity: isDel ? 0.4 : 1, transition:'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Score */}
                  <td style={{ ...TD, textAlign:'center' }}>
                    <div style={{
                      display:'inline-flex', flexDirection:'column', alignItems:'center',
                      justifyContent:'center', width:'42px', height:'42px', borderRadius:'7px',
                      background: hc.bg, border:`1px solid ${hc.br}`,
                    }}>
                      <span style={{ fontSize:'13px', fontWeight:800, color: hc.c, lineHeight:1, fontFamily:'StevieSans,sans-serif' }}>
                        {r.engineHealth ?? '—'}
                      </span>
                      <span style={{ fontSize:'8px', color: hc.c, opacity:0.65 }}>/100</span>
                    </div>
                  </td>
                  {/* Engine */}
                  <td style={{ ...TD, fontWeight:600, color:'var(--t1)' }}>
                    {r.engineLabel || '—'}
                  </td>
                  {/* Type */}
                  <td style={TD}>
                    {r.engineType ? (
                      <span style={{
                        padding:'2px 8px', fontSize:'10px', fontWeight:600,
                        textTransform:'uppercase', letterSpacing:'0.05em', borderRadius:'4px',
                        background:'var(--fill-info)', border:'1px solid var(--br-info)', color:'var(--c-info)',
                        whiteSpace:'nowrap',
                      }}>{r.engineType}</span>
                    ) : '—'}
                  </td>
                  {/* Load */}
                  <td style={{ ...TD, whiteSpace:'nowrap' }}>
                    {r.testLoadPct != null
                      ? <span style={{ fontFamily:'monospace', fontWeight:600 }}>{Number(r.testLoadPct).toFixed(1)} %</span>
                      : '—'}
                  </td>
                  {/* Date */}
                  <td style={{ ...TD, whiteSpace:'nowrap', color:'var(--t3)' }}>
                    {fmt(r.savedAt)}
                  </td>
                  {/* Findings */}
                  <td style={TD}>
                    <FindingsBadge critical={r.criticalCount} warning={r.warningCount} />
                  </td>
                  {/* Actions */}
                  <td style={{ ...TD, textAlign:'center' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display:'inline-flex', alignItems:'center', gap:'5px' }}>
                      {/* <button
                        onClick={() => handleOpenReport(r, 'engine')}
                        disabled={isOpening || isDel}
                        title="Open report"
                        style={{
                          display:'inline-flex', alignItems:'center', gap:'4px',
                          padding:'5px 11px', fontSize:'11px', fontWeight:600, borderRadius:'5px',
                          cursor: isOpening ? 'wait' : 'pointer',
                          background:'var(--btn-p-bg)', border:'1px solid var(--btn-p-b)', color:'var(--btn-p-c)',
                          opacity: isOpening ? 0.5 : 1, whiteSpace:'nowrap',
                        }}
                      >
                        <ChevronRight style={{ width:'11px', height:'11px' }} />
                        {isOpening ? 'Opening…' : 'Open'}
                      </button> */}
                      <button
                        onClick={() => handleDownloadReport(r)}
                        disabled={isOpening || isDel || isDownloading}
                        title={isDownloading ? 'Generating PDF…' : 'Download report as PDF'}
                        style={{
                          display:'inline-flex', alignItems:'center', justifyContent:'center',
                          width:'28px', height:'28px', borderRadius:'5px',
                          cursor: isDownloading ? 'wait' : 'pointer',
                          background:'var(--bg-surface)', border:'1px solid var(--br-mid)', color:'var(--t2)',
                          flexShrink:0, opacity: isDownloading ? 0.6 : 1,
                          transition:'opacity 0.15s',
                        }}
                      >
                        {isDownloading
                          ? <span style={{
                              width:'10px', height:'10px', borderRadius:'50%',
                              border:'2px solid var(--br-mid)',
                              borderTopColor:'var(--accent)',
                              display:'inline-block',
                              animation:'zq-spin-fade-simple 0.7s linear infinite',
                            }} />
                          : <Download style={{ width:'12px', height:'12px' }} />
                        }
                      </button>
                      <button
                        onClick={() => handleDeleteReport(r)}
                        disabled={isOpening || isDel}
                        title="Delete report"
                        style={{
                          display:'inline-flex', alignItems:'center', justifyContent:'center',
                          width:'28px', height:'28px', borderRadius:'5px',
                          cursor: isDel ? 'wait' : 'pointer',
                          background:'var(--fill-err)', border:'1px solid var(--br-err)', color:'var(--c-err)',
                          flexShrink:0, opacity: isDel ? 0.5 : 1,
                        }}
                      >
                        <Trash2 style={{ width:'12px', height:'12px' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Accordion wrapper ─────────────────────────────────────────────────────────
  const AccordionSection = ({ icon: Ic, label, count, isOpen, onToggle, children }) => (
    <div style={{ background:'var(--bg-panel)', border:'1px solid var(--br)', borderRadius:'10px', overflow:'hidden', marginBottom:'16px' }}>
      <button
        onClick={onToggle}
        style={{
          width:'100%', display:'flex', alignItems:'center', gap:'12px',
          padding:'14px 20px', background:'none', border:'none', cursor:'pointer',
          borderBottom: isOpen ? '1px solid var(--br)' : 'none', transition:'background 0.12s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        <div style={{
          width:'32px', height:'32px', borderRadius:'7px', flexShrink:0,
          background:'var(--fill-ok)', border:'1px solid var(--br-ok)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <Ic style={{ width:'15px', height:'15px', color:'var(--accent)' }} />
        </div>
        <span style={{ fontSize:'14px', fontWeight:700, color:'var(--t1)', fontFamily:'StevieSans,sans-serif', flex:1, textAlign:'left' }}>
          {label}
        </span>
        <span style={{
          padding:'2px 10px', fontSize:'11px', fontWeight:600, borderRadius:'20px',
          background:'var(--bg-surface)', border:'1px solid var(--br)', color:'var(--t3)', marginRight:'8px',
        }}>
          {count} report{count !== 1 ? 's' : ''}
        </span>
        <ChevronDown style={{
          width:'15px', height:'15px', color:'var(--t3)', flexShrink:0,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.2s',
        }} />
      </button>
      {isOpen && <div>{children}</div>}
    </div>
  );

  return (
    <div className="min-h-screen" data-theme={darkMode ? 'dark' : 'light'}
      style={{ background:'var(--bg-page)', color:'var(--t1)' }}>
      <style>{BROWSER_THEME}</style>
      {/* Overlay spinner — report list loading or PDF download in progress */}
      {(loadingReports || downloadingId) && <LoadingSpinner overlay />}
      {renderHeader()}

      <div style={{ padding:'28px 24px', width:'100%', boxSizing:'border-box' }}>

        {/* Sub-header count */}
        {!loadingReports && (
        <p style={{ fontSize:'13px', color:'var(--t3)', marginBottom:'20px' }}>
          {vesselReports.length === 0
            ? 'No saved reports for this vessel yet.'
            : `${vesselReports.length} saved report${vesselReports.length !== 1 ? 's' : ''} — most recent first.`
          }
        </p>
        )}

        {/* Main Engine accordion */}
        <AccordionSection
          icon={Cpu} label="Main Engine" count={meReports.length}
          isOpen={meOpen} onToggle={() => setMeOpen(o => !o)}
        >
          <ReportTable reports={meReports} emptyLabel="No main engine reports saved for this vessel." />
        </AccordionSection>

        {/* Aux Engine accordion */}
        <AccordionSection
          icon={Wrench} label="Auxiliary Engine" count={aeReports.length}
          isOpen={aeOpen} onToggle={() => setAeOpen(o => !o)}
        >
          <ReportTable reports={aeReports} emptyLabel="No auxiliary engine reports saved for this vessel." />
        </AccordionSection>

        {/* Back button */}
        <div style={{ marginTop:'8px' }}>
          <button
            onClick={() => { setView('vessels'); onClearReportVessel?.(); }}
            style={{
              display:'inline-flex', alignItems:'center', gap:'6px',
              padding:'7px 16px', fontSize:'12px', fontWeight:600,
              borderRadius:'6px', cursor:'pointer',
              background:'var(--bg-surface)', border:'1px solid var(--br-mid)', color:'var(--t2)',
            }}
          >
            <ChevronLeft style={{ width:'13px', height:'13px' }} />
            Back to Fleet
          </button>
        </div>
      </div>
    </div>
  );
}